/**
 * ActivationFunnelChart Component
 * Displays user activation funnel visualization
 *
 * Story 5.1: New User Activation Funnel
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronUp, User, ArrowRight } from 'lucide-react';
import { Card } from '../../common';
import type { ActivationFunnelData, ActivationFunnelStep } from '../../../services/reportApi';

export interface ActivationFunnelChartProps {
  /** Funnel data */
  data: ActivationFunnelData;
  /** Loading state */
  isLoading?: boolean;
  /** Click handler for user */
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const FunnelSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="space-y-3">
      {[100, 85, 70, 55, 40].map((width, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-12 bg-stone-200 rounded" style={{ width: `${width}%` }} />
          <div className="h-4 bg-stone-200 rounded w-16" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Funnel step component
 */
const FunnelStep: React.FC<{
  step: ActivationFunnelStep;
  maxWidth: number;
  isLast: boolean;
}> = ({ step, maxWidth, isLast }) => {
  const widthPercent = (step.percentage / 100) * maxWidth;

  // Color based on position and conversion
  const getStepColor = () => {
    if (step.conversionRate >= 80) return 'bg-green-500';
    if (step.conversionRate >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-4">
        {/* Step bar */}
        <div className="flex-1">
          <div
            className={clsx(
              'h-12 rounded-lg flex items-center px-4 transition-all duration-500',
              getStepColor()
            )}
            style={{ width: `${Math.max(widthPercent, 20)}%` }}
          >
            <span className="text-white font-medium text-sm truncate">
              {step.label}
            </span>
          </div>
        </div>

        {/* Metrics */}
        <div className="w-32 text-right">
          <div className="text-lg font-bold text-stone-900">{step.count}</div>
          <div className="text-xs text-stone-500">{step.percentage}%</div>
        </div>
      </div>

      {/* Drop-off indicator */}
      {!isLast && step.dropOffRate > 0 && (
        <div className="absolute left-4 -bottom-2 flex items-center gap-1 text-xs text-red-600">
          <ArrowRight className="w-3 h-3 rotate-90" />
          <span>{step.dropOffRate}% drop-off</span>
        </div>
      )}
    </div>
  );
};

/**
 * Activation Funnel Chart Component
 */
export const ActivationFunnelChart: React.FC<ActivationFunnelChartProps> = ({
  data,
  isLoading = false,
  onUserClick,
  className,
}) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Activation Funnel</h3>
        <FunnelSkeleton />
      </Card>
    );
  }

  const { steps, totalUsers, fullyActivatedCount, fullyActivatedRate, usersStuckAtStep } = data;

  if (steps.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Activation Funnel</h3>
        <div className="text-center py-8 text-stone-500">
          <User className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No funnel data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Activation Funnel</h3>
          <p className="text-sm text-stone-500">
            {fullyActivatedCount} of {totalUsers} users fully activated ({fullyActivatedRate}%)
          </p>
        </div>
      </div>

      {/* Funnel visualization */}
      <div className="space-y-6 mb-6">
        {steps.map((step, index) => (
          <FunnelStep
            key={step.step}
            step={step}
            maxWidth={100}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>

      {/* Users stuck at steps */}
      {usersStuckAtStep.some(s => s.users.length > 0) && (
        <div className="border-t border-stone-200 pt-4">
          <h4 className="text-sm font-medium text-stone-700 mb-3">Users Needing Attention</h4>
          <div className="space-y-2">
            {usersStuckAtStep.map((stepData) => {
              if (stepData.users.length === 0) return null;
              const isExpanded = expandedStep === stepData.step;

              return (
                <div key={stepData.step} className="border border-stone-200 rounded-lg">
                  <button
                    onClick={() => setExpandedStep(isExpanded ? null : stepData.step)}
                    className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-stone-700">
                        Stuck at: {steps.find(s => s.step === stepData.step)?.label || stepData.step}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {stepData.users.length} users
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-stone-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-stone-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3">
                      <div className="space-y-2">
                        {stepData.users.slice(0, 5).map((user) => (
                          <button
                            key={user.userId}
                            onClick={() => onUserClick?.(user.userId)}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-stone-100 transition-colors text-left"
                          >
                            <div>
                              <div className="text-sm font-medium text-stone-900">{user.name}</div>
                              <div className="text-xs text-stone-500">{user.email}</div>
                            </div>
                            <span className="text-xs text-stone-400">
                              {user.daysSinceSignup}d ago
                            </span>
                          </button>
                        ))}
                        {stepData.users.length > 5 && (
                          <p className="text-xs text-stone-500 text-center pt-2">
                            +{stepData.users.length - 5} more users
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ActivationFunnelChart;
